import { useRoutes, Link, useLocation } from 'react-router-dom'
import { useAuth, canAccess, signOut } from '@/hooks/useAuth'
import { useTheme } from '@/contexts/ThemeContext'
import { lumi } from '@/lib/lumi'
import {Sun, Moon, LogOut, Image, Users, ChevronRight, LayoutDashboard, Home, Loader2, Award, MessageSquare, Layers, LayoutTemplate, Heart, Inbox, UserCircle2, } from 'lucide-react'
import toast from 'react-hot-toast'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import type { UserModuleAccess } from '@/types'

import ProtectedRoute from '@/components/ProtectedRoute'
import DashboardHome from '@/pages/admin/DashboardHome'
import ManageBanners from '@/pages/admin/ManageBanners'
import ManageUsers from '@/pages/admin/ManageUsers'
import SocialSettings from '@/pages/admin/SocialSettings'
import LandingSettings from '@/pages/admin/LandingSettings'
import ManageTestimonials from '@/pages/admin/ManageTestimonials'
import ManagePartners from '@/pages/admin/ManagePartners'
import ManageDonations from '@/pages/admin/ManageDonations'
import ManageContacts from '@/pages/admin/ManageContacts'
import ManageTeam from '@/pages/admin/ManageTeam'
import type { SiteSetting } from '@/types'

// Rotas com ProtectedRoute usando o módulo correspondente
const adminRoutes = [
  { path: '/',            element: <ProtectedRoute require="any">    <DashboardHome /></ProtectedRoute> },
  { path: '/banners',     element: <ProtectedRoute require="banners"><ManageBanners /></ProtectedRoute> },
  { path: '/usuarios',    element: <ProtectedRoute require="admin">  <ManageUsers /></ProtectedRoute> },
  { path: '/redes-sociais',element:<ProtectedRoute require="redes-sociais"><SocialSettings /></ProtectedRoute> },
  { path: '/landing',     element: <ProtectedRoute require="landing"><LandingSettings /></ProtectedRoute> },
  { path: '/depoimentos', element: <ProtectedRoute require="depoimentos"><ManageTestimonials /></ProtectedRoute> },
  { path: '/parceiros',   element: <ProtectedRoute require="parceiros"><ManagePartners /></ProtectedRoute> },
  { path: '/doacoes',     element: <ProtectedRoute require="doacoes"><ManageDonations /></ProtectedRoute> },
  { path: '/mensagens',   element: <ProtectedRoute require="mensagens"><ManageContacts /></ProtectedRoute> },
  { path: '/equipe',      element: <ProtectedRoute require="equipe"> <ManageTeam /></ProtectedRoute> },
]

// Cada item de nav tem: path, label, icon, module (null = acesso universal, 'admin' = só admin)
interface NavItem {
  path:    string
  label:   string
  icon:    React.ElementType
  exact?:  boolean
  module:  UserModuleAccess | 'admin' | null
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    label: 'Painel',
    items: [
      { path: '/admin', label: 'Visão Geral', icon: LayoutDashboard, exact: true, module: null },
    ],
  },
  {
    label: 'Landing Page',
    items: [
      { path: '/admin/landing',      label: 'Configurar Landing',   icon: LayoutTemplate, module: 'landing' },
      { path: '/admin/equipe',       label: 'Nossa Equipe',         icon: UserCircle2,    module: 'equipe' },
      { path: '/admin/depoimentos',  label: 'Depoimentos',          icon: MessageSquare,  module: 'depoimentos' },
      { path: '/admin/parceiros',    label: 'Parceiros',            icon: Award,          module: 'parceiros' },
      { path: '/admin/doacoes',      label: 'Doações',              icon: Heart,          module: 'doacoes' },
      { path: '/admin/mensagens',    label: 'Mensagens de Contato', icon: Inbox,          module: 'mensagens' },
    ],
  },
  {
    label: 'Site',
    items: [
      { path: '/admin/banners',      label: 'Banners',              icon: Image,          module: 'banners' },
      { path: '/admin/redes-sociais',label: 'Redes Sociais',        icon: Layers,         module: 'redes-sociais' },
    ],
  },
  {
    label: 'Administração',
    items: [
      { path: '/admin/usuarios', label: 'Utilizadores', icon: Users, module: 'admin' },
    ],
  },
]

function AdminRoutes() {
  return useRoutes(adminRoutes)
}

export default function Dashboard() {
  const { isAuthenticated, isLoading, isAdmin, user } = useAuth()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const [logoImage, setLogoImage] = useState<string>('')
  const [siteTitle, setSiteTitle] = useState('PALACIO DA SABEDORIA')

  useEffect(() => {
    Promise.all([
      lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
      lumi.entities.site_settings.list({ filter: { key: 'site_title' },  limit: 1 }),
    ]).then(([logoRes, titleRes]) => {
      if (logoRes.list[0]) setLogoImage((logoRes.list[0] as SiteSetting).value)
      if (titleRes.list[0]) setSiteTitle((titleRes.list[0] as SiteSetting).value)
    }).catch(() => {/* silencioso */})
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin" style={{ color: '#5BBDB5' }} />
          <p className="text-sm text-gray-500 dark:text-gray-400">A verificar autenticação...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />
  }

  const handleLogout = async () => {
    signOut()
    toast.success('Sessão encerrada com sucesso.')
  }

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path
    return location.pathname.startsWith(path)
  }

  /** Decide se um item de menu deve ser visível para o utilizador actual */
  const isMenuVisible = (item: NavItem): boolean => {
    if (isAdmin) return true                          // admin vê tudo
    if (item.module === null) return true             // visão geral: todos vêem
    if (item.module === 'admin') return false         // só admin
    return canAccess(user, item.module as UserModuleAccess) // verifica view/edit/delete
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex transition-colors">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col min-h-screen sticky top-0 h-screen overflow-y-auto">

        {/* Logo / Branding */}
        <div className="p-5 border-b border-gray-100 dark:border-gray-800">
          <Link to="/" className="flex items-center gap-2.5 mb-1">
            {logoImage ? (
              <img src={logoImage} alt={siteTitle} className="h-8 w-auto object-contain" />
            ) : (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#5BBDB5' }}
              >
                <span className="text-white font-black text-xs">PS</span>
              </div>
            )}
            <div className="min-w-0">
              <p className="font-bold text-gray-900 dark:text-white text-sm leading-tight truncate">{siteTitle}</p>
              <p className="text-xs text-gray-400">Painel Administrativo</p>
            </div>
          </Link>
        </div>

        {/* Badge de nível */}
        <div className="px-3 pt-3">
          {isAdmin ? (
            <div className="px-3 py-1.5 rounded-lg flex items-center gap-2" style={{ background: '#DCF0EE' }}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#5BBDB5' }} />
              <span className="text-xs font-bold" style={{ color: '#1e7a74' }}>Administrador</span>
            </div>
          ) : (
            <div className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
              <span className="text-xs font-bold text-blue-700 dark:text-blue-400">Utilizador</span>
            </div>
          )}
        </div>

        {/* Nav por grupos */}
        <nav className="flex-1 py-4 px-3 space-y-5 overflow-y-auto" aria-label="Menu administrativo">
          {navGroups.map(group => {
            const visibleItems = group.items.filter(isMenuVisible)
            if (visibleItems.length === 0) return null
            return (
              <div key={group.label}>
                <p className="px-3 mb-1 text-xs font-bold text-gray-400 uppercase tracking-wider">{group.label}</p>
                <ul className="space-y-0.5">
                  {visibleItems.map(item => {
                    const Icon   = item.icon
                    const active = isActive(item.path, item.exact)
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            active
                              ? 'font-semibold text-white'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                          }`}
                          style={active ? { background: '#5BBDB5' } : {}}
                          aria-current={active ? 'page' : undefined}
                        >
                          <Icon size={16} />
                          <span className="flex-1">{item.label}</span>
                          {active && <ChevronRight size={14} />}
                        </Link>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </nav>

        {/* Rodapé da sidebar */}
        <div className="p-3 border-t border-gray-100 dark:border-gray-800 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
          >
            <Home size={16} />
            <span>Ver Site</span>
          </a>
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
            aria-label="Alternar tema"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}</span>
          </button>

          {/* Utilizador + logout */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#DCF0EE' }}
            >
              <span className="text-xs font-bold" style={{ color: '#5BBDB5' }}>
                {user?.displayName?.charAt(0)?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                {user?.displayName || 'Utilizador'}
              </p>
              <p className="text-xs text-gray-400 truncate">{user?.username || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
              aria-label="Sair"
              title="Terminar sessão"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Conteúdo principal */}
      <main className="flex-1 overflow-auto min-h-screen">
        <AdminRoutes />
      </main>
    </div>
  )
}
